import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateIntercompanyTransactionInput, IntercompanyTransaction } from '@accounting/shared';
import { api } from '../lib/api';

export function useIntercompanyTransactions(companyId?: string) {
  return useQuery({
    queryKey: ['intercompany', companyId ?? 'all'],
    queryFn: () =>
      api
        .get<{ transactions: IntercompanyTransaction[] }>('/intercompany', companyId ? { companyId } : undefined)
        .then((r) => r.transactions),
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  // Intercompany touches two companies, so invalidate broadly.
  for (const key of ['intercompany', 'journal-entries', 'accounts-balances', 'report', 'command-center']) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}

export function useCreateIntercompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateIntercompanyTransactionInput) =>
      api.post<{ transaction: IntercompanyTransaction }>('/intercompany', input).then((r) => r.transaction),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useVoidIntercompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<{ transaction: IntercompanyTransaction }>(`/intercompany/${id}/void`, { reason }).then((r) => r.transaction),
    onSuccess: () => invalidateAll(qc),
  });
}
